import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { Lambda, reaction } from 'mobx';
import { RootStores, ProjectsRootVisualStore, ProjectsStore, RouterStore } from '../modules/common/stores';
import { ProjectStores } from '../modules/project_management/stores';
import { ProjectState, Project } from '../modules/common/models';
import { STORE_PROJECTS_ROOT_UI, STORE_ROUTER } from '../modules/common/constants';
import { STORE_PROJECT_EXPORT } from '../modules/project_management/constants';
import { AppPermissions } from '../modules/authorization/Permissions';
import { hasPermission } from '../modules/authorization/components/HasPermission';
import { Menu, Tooltip } from 'antd';
import {
    AuditOutlined,
    HistoryOutlined,
    BlockOutlined,
    CodeOutlined,
    DeliveredProcedureOutlined,
    ContainerOutlined,
    BarChartOutlined,
    FolderOpenOutlined,
    SettingOutlined,
    FileTextOutlined,
    ExperimentOutlined
} from '@ant-design/icons';
import ProjectExportStore from '../modules/project_management/stores/ProjectExportStore';
import ProjectExportDialog from '../modules/project_management/components/ProjectExportDialog';
import { ProjectTitle } from '../components';
import { UserProfileStore } from '../modules/common/stores';

const SubMenu = Menu.SubMenu;

const initialState = {
    openKeys: new Array<string>(),
    selectedKeys: new Array<string>(),
    selectedItemKey: ''
};

export type State = Readonly<typeof initialState>;
type Permissions = {
    permissions: string[];
    objectPermissions: { [id: string]: string[] };
};

type Props = Partial<RootStores> & ProjectStores & Permissions;

export class ProjectsList extends React.Component<Props, State> {
    readonly state: State = initialState;

    private projectsRootStore: ProjectsRootVisualStore;
    private store: ProjectsStore;
    private router: RouterStore;
    private projectExportStore: ProjectExportStore;
    private userProfileStore: UserProfileStore;
    private readonly disposer: Lambda;

    constructor(props: Props) {
        super(props);
        this.projectsRootStore = this.props.projectsRootUI!;
        this.store = this.projectsRootStore.projectsStore;
        this.router = this.props.router!;
        this.projectExportStore = this.props.projectExport!;
        this.userProfileStore = this.props.userProfileStore!;

        const d1 = this.store.projectsStates?.observe(() => {
            let changed = false;
            this.store.projectsStates?.forEach((v, p) => {
                if (v === ProjectState.Processing) {
                    if (this.projectsRootStore.currentProject) {
                        changed = this.projectsRootStore.currentProject.id !== p || changed;
                    }
                }
            });

            if (changed) {
                this.projectsRootStore.navigateToProjectsPage();
            }
        });

        const d2 = reaction(
            () => this.projectsRootStore.currentProject,
            p => {
                if (!p) {
                    this.setState({ ...this.state, openKeys: new Array<string>() });
                } else if (!sessionStorage.getItem('menuOpenItems')) {
                    this.setState({ ...this.state, openKeys: [p.id] });
                }
            }
        );

        const d3 = reaction(
            () => this.state.openKeys,
            k => {
                sessionStorage.setItem('menuOpenItems', JSON.stringify(k));
            }
        );

        const d4 = reaction(
            () => this.state.selectedItemKey,
            k => {
                sessionStorage.setItem('selectedItem', k);
            }
        );

        if (sessionStorage.getItem('menuOpenItems')) {
            reaction(
                () => this.store.projects,
                (projects, r) => {
                    this.onOpenChange(JSON.parse(sessionStorage.getItem('menuOpenItems')!));
                    r.dispose();
                }
            );
        }

        if (sessionStorage.getItem('selectedItem')) {
            reaction(
                () => this.store.projects,
                (projects, r) => {
                    this.onSelectedChange(sessionStorage.getItem('selectedItem')!);
                    r.dispose();
                }
            );
        }

        this.disposer = () => {
            if (d1) {
                d1();
            }

            d2();
            d3();
            d4();
        };
    }

    componentWillUnmount() {
        this.disposer();
    }

    onOpenChange = (openKeys: string[]) => {
        const submenus = ['ApplicationConfiguration', 'DataExtraction', 'Documents', 'Analytics', 'ProjectSettings'];
        if (openKeys.some(k => submenus.filter(s => k.endsWith(s)).length === 0)) {
            this.router.setIsFooterHidden(true);
        } else {
            this.router.setIsFooterHidden(false);
        }

        if (openKeys.length && submenus.some(s => openKeys.slice(-1)[0].endsWith(s))) {
            this.setState({ openKeys: openKeys });
            return;
        }

        if (!openKeys || openKeys.length === 0) {
            this.setState({ openKeys: [] });
            return;
        }

        const lastKey = openKeys.slice(-1);
        let newKeys: string[] = [];

        if (openKeys.length >= this.state.openKeys.length) {
            newKeys = submenus.map(s => `${lastKey}${s}`);
            newKeys = [...lastKey, ...newKeys];
        } else {
            newKeys = [...lastKey];
        }

        this.setState({ openKeys: newKeys });
    };

    onSelectedChange = (selectedItem: string) => {
        this.setState({ selectedItemKey: selectedItem });
    };

    onSelectMenuItem = (key: string, type: string, project: Project) => {
        this.setState(s => ({ ...s, selectedKeys: [key], selectedItemKey: project.id + key }));
        if (type != null) {
            switch (type) {
                case 'textBlocks':
                    this.projectsRootStore.navigateToTextBlocksPage(project);
                    return;
                case 'visualBlocks':
                    this.projectsRootStore.navigateToVisualBlocksPage(project);
                    return;
                case 'uploadedPackages':
                    this.projectsRootStore.navigateToUploadedPackagesPage(project);
                    return;
                case 'administration':
                    this.projectsRootStore.navigateToProjectAdministrationPage(project);
                    return;
                case 'mlmodels':
                    this.projectsRootStore.navigateToMlModelsPage(project);
                    return;
                case 'applicationDefinitions':
                    this.projectsRootStore.navigateToApplicationDefinitionsPage(project);
                    return;
                case 'connectionsDefinitions':
                    this.projectsRootStore.navigateToConnectionsDefinitionsPage(project);
                    return;
                case 'sessions':
                    this.projectsRootStore.navigateToSessionsPage(project);
                    return;
                case 'rules':
                    this.projectsRootStore.navigateToRulesPage(project);
                    return;
                case 'externalConnections':
                    this.projectsRootStore.navigateToExternalConnectionsPage(project);
                    return;
                case 'problemReports':
                    this.projectsRootStore.navigateToPackageProblemReportsPage(project);
                    return;
                case 'referenceData':
                    this.projectsRootStore.navigateToReferenceDataPage(project);
                    return;
                case 'iotaAudit':
                    this.projectsRootStore.navigateToIotaAuditPage(project);
                    return;
                case 'apiFields':
                    this.projectsRootStore.navigateToFieldBindingsPage(project);
                    return;
                case 'forms':
                    this.projectsRootStore.navigateToFormTypesPage(project);
                    return;
                case 'projectSettings':
                    this.projectsRootStore.navigateToProjectSettingsPage(project);
                    return;
                case 'testProjects':
                    this.projectsRootStore.navigateToTestProjectPage(project);
                    return;
                case 'history':
                    this.projectsRootStore.navigateToHistoryPage(project);
                    return;
                case 'instructWorkflows':
                    this.projectsRootStore.navigateToInstructWorkflowsPage(project);
                    return;
                case 'schemaGenerator':
                    this.projectsRootStore.navigateToSchemaGeneratorPage(project);
                    return;
                case 'dataCrafterTemplates':
                    this.projectsRootStore.navigateToDataCrafterTemplatesPage(project);
                    return;
                case 'promptTemplates':
                    this.projectsRootStore.navigateToPromptTemplatesPage(project);
                    return;
                case 'trainingProjects':
                    this.projectsRootStore.navigateToTrainingProjectsPage(project);
                    return;
                default:
                    return;
            }
        }
    };

    render() {
        const filteredProjects = this.store.filteredProjects;

        // eslint-disable-next-line max-len
        const menuItem = (
            type: string,
            p: Project,
            icon: React.ReactElement,
            title: string,
            enabled: boolean,
            style: object | undefined = undefined,
            userPermissions?: string[],
            permissionClaim?: AppPermissions
        ) => {
            if (!userPermissions || hasPermission(userPermissions, permissionClaim!)) {
                return (
                    <Menu.Item
                        className={this.state.selectedItemKey === p.id + type ? 'selected' : ''}
                        key={p.id + type}
                        data-id={`${p.title}_${type}`}
                        disabled={!enabled}
                        onClick={() => this.onSelectMenuItem(type, type, p)}
                    >
                        <span style={style}>
                            <Tooltip placement="right" title={title}>
                                {icon}
                            </Tooltip>
                            <span>{title}</span>
                        </span>
                    </Menu.Item>
                );
            } else {
                return <></>;
            }
        };

        const makeApplicationConfigurationItem = (p: Project) => {
            return (
                <SubMenu
                    key={p.id + 'ApplicationConfiguration'}
                    title={
                        <span>
                            <Tooltip placement="right" title="Application Configuration">
                                <i className="alpha-icon md wrench-white-icon" />
                            </Tooltip>
                            <span>{'Application Configuration'}</span>
                        </span>
                    }
                >
                    {menuItem(
                        'applicationDefinitions',
                        p,
                        <i className="alpha-icon md applications-icon" />,
                        'IOTA Apps',
                        true
                    )}
                    {menuItem(
                        'apiFields',
                        p,
                        <i className="alpha-icon md connection-icon" />,
                        'API Fields',
                        true,
                        undefined,
                        getProjectPermissions(p.id),
                        AppPermissions.CanManageApiFields
                    )}
                    {menuItem(
                        'externalConnections',
                        p,
                        <i className="alpha-icon md connection-icon" />,
                        'External Connections',
                        true
                    )}
                </SubMenu>
            );
        };

        const makeDataExtractionItem = (p: Project) => {
            return (
                <SubMenu
                    key={p.id + 'DataExtraction'}
                    title={
                        <span>
                            <Tooltip placement="right" title="Data Extraction">
                                <FileTextOutlined />
                            </Tooltip>
                            <span>{'Data Extraction'}</span>
                        </span>
                    }
                >
                    {menuItem('rules', p, <i className="alpha-icon md label-icon" />, 'Rules', true)}
                    {menuItem(
                        'promptTemplates',
                        p,
                        <ContainerOutlined style={{ marginLeft: 3 }} />,
                        'Prompt templates',
                        true,
                        undefined,
                        getProjectPermissions(p.id),
                        AppPermissions.CanManagePromptTemplates
                    )}
                    {menuItem(
                        'testProjects',
                        p,
                        <i className="alpha-icon md gear-icon" />,
                        'Test projects',
                        true,
                        undefined,
                        getProjectPermissions(p.id),
                        AppPermissions.CanManageTestProjects
                    )}
                    {menuItem(
                        'referenceData',
                        p,
                        <i className="alpha-icon md ref-data-icon" />,
                        'Reference data',
                        true
                    )}
                    {menuItem(
                        'dataCrafterTemplates',
                        p,
                        <DeliveredProcedureOutlined style={{ marginLeft: 3 }} />,
                        'Data crafter',
                        true,
                        undefined,
                        getProjectPermissions(p.id),
                        AppPermissions.CanManageDataCrafterTemplates
                    )}
                    {makeInstructWorkflowsItem(p, getProjectPermissions(p.id))}
                    {menuItem(
                        'schemaGenerator',
                        p,
                        <CodeOutlined style={{ marginLeft: 3 }} />,
                        'Schema Generator',
                        true
                    )}
                    {menuItem(
                        'forms',
                        p,
                        <AuditOutlined />,
                        'Forms',
                        true,
                        undefined,
                        permissions,
                        AppPermissions.CanManageFormTemplates
                    )}
                    {menuItem('mlmodels', p, <i className="alpha-icon md ml-storage-icon" />, 'ML Models', true)}
                    {makeTrainingProjectsItem(p, permissions)}
                </SubMenu>
            );
        };

        const makeDocumentsItem = (p: Project) => {
            return (
                <SubMenu
                    key={p.id + 'Documents'}
                    title={
                        <span>
                            <Tooltip placement="right" title="Documents">
                                <FolderOpenOutlined />
                            </Tooltip>
                            <span>{'Documents'}</span>
                        </span>
                    }
                >
                    {menuItem('uploadedPackages', p, <i className="alpha-icon md import-icon" />, 'Upload', true)}
                    {menuItem('textBlocks', p, <i className="alpha-icon md label-icon" />, 'Text Blocks', true)}
                    {menuItem(
                        'visualBlocks',
                        p,
                        <i className="alpha-icon md interactive-labels-icon" />,
                        'Visual Blocks',
                        true
                    )}
                </SubMenu>
            );
        };

        const makeAnalyticsItem = (p: Project) => {
            return (
                <SubMenu
                    key={p.id + 'Analytics'}
                    title={
                        <span>
                            <Tooltip placement="right" title="Analytics">
                                <BarChartOutlined />
                            </Tooltip>
                            <span>{'Analytics'}</span>
                        </span>
                    }
                >
                    {menuItem('sessions', p, <i className="alpha-icon md session-icon" />, 'Sessions', true)}
                    {menuItem('iotaAudit', p, <AuditOutlined />, 'Audit', true)}
                </SubMenu>
            );
        };

        const makeProjectSettingsItem = (p: Project, userPermissions: string[]) => {
            const canAccessAdmin = hasPermission(userPermissions, AppPermissions.CanAccessAdministration);
            const canEditProject = hasPermission(userPermissions, AppPermissions.CanAddEditDeleteAssignProject);

            if (!userPermissions || !(canAccessAdmin || canEditProject)) {
                return null;
            }

            return (
                <SubMenu
                    key={p.id + 'ProjectSettings'}
                    title={
                        <span>
                            <Tooltip placement="right" title="Project Settings">
                                <SettingOutlined />
                            </Tooltip>
                            <span>{'Project Settings'}</span>
                        </span>
                    }
                >
                    {canEditProject &&
                        menuItem('projectSettings', p, <i className="alpha-icon md gear-icon" />, 'Settings', true)}
                    {canAccessAdmin &&
                        menuItem('history', p, <HistoryOutlined style={{ marginLeft: 3 }} />, 'Change History', true)}
                </SubMenu>
            );
        };

        const makeInstructWorkflowsItem = (p: Project, userPermissions: string[]) => {
            if (!userPermissions || !hasPermission(userPermissions, AppPermissions.CanEditMlStorage)) {
                return null;
            }

            return (
                <React.Fragment>
                    <Menu.Divider className="collapsed-divider" />
                    {menuItem(
                        'instructWorkflows',
                        p,
                        <BlockOutlined style={{ marginLeft: 3 }} />,
                        'Instruct Workflows',
                        true
                    )}
                </React.Fragment>
            );
        };

        const makeTrainingProjectsItem = (p: Project, userPermissions: string[]) => {
            if (!userPermissions || !hasPermission(userPermissions, AppPermissions.CanEditMlTraining)) {
                return null;
            }

            return (
                <React.Fragment>
                    <Menu.Divider className="collapsed-divider" />
                    {menuItem(
                        'trainingProjects',
                        p,
                        <ExperimentOutlined style={{ marginLeft: 3 }} />,
                        'Training Projects',
                        true
                    )}
                </React.Fragment>
            );
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const getExpandIcon = (props: any) => {
            let style: React.CSSProperties = {
                float: 'right',
                transition: 'transform .3s'
            };

            if (!props.isOpen) {
                style.transform = 'rotate(-90deg)';
            }

            return <i style={style} className="alpha-icon md expand-icon" />;
        };

        const { permissions, objectPermissions } = this.props;

        const getProjectPermissions = (projectId: string) => {
            return [...new Set([...permissions, ...(objectPermissions[projectId] || [])])];
        };

        this.store.setCurrentUserRoles(permissions);
        this.store.setCurrentUserObjectPermissions(objectPermissions);

        return (
            <>
                <Menu
                    onOpenChange={this.onOpenChange}
                    mode="inline"
                    openKeys={this.state.openKeys}
                    selectedKeys={this.state.selectedKeys}
                    style={{ overflow: 'hidden auto', borderRight: 0 }}
                    inlineIndent={18}
                    expandIcon={getExpandIcon}
                >
                    {filteredProjects.map(p => (
                        <React.Fragment key={p.id + '-fragment'}>
                            <SubMenu
                                data-id={p.id}
                                data-id-type="MainMenu"
                                data-id-name={p.title}
                                key={p.id}
                                title={
                                    <ProjectTitle
                                        project={p}
                                        projectsRootStore={this.projectsRootStore}
                                        routerStore={this.router}
                                        userProfileStore={this.userProfileStore}
                                    />
                                }
                                className="main-menu-item"
                            >
                                {makeApplicationConfigurationItem(p)}
                                <Menu.Divider className="collapsed-divider" />
                                {makeDataExtractionItem(p)}
                                <Menu.Divider className="collapsed-divider" />
                                {makeDocumentsItem(p)}
                                <Menu.Divider className="collapsed-divider" />
                                {makeAnalyticsItem(p)}
                                <Menu.Divider className="collapsed-divider" />
                                <Menu.Divider className="collapsed-divider" />
                                {makeProjectSettingsItem(p, permissions)}
                                <Menu.Divider className="collapsed-divider" />
                            </SubMenu>
                            <Menu.Divider />
                        </React.Fragment>
                    ))}
                </Menu>

                <ProjectExportDialog store={this.projectExportStore} />
            </>
        );
    }
}

export default inject(STORE_PROJECTS_ROOT_UI, STORE_ROUTER, STORE_PROJECT_EXPORT)(observer(ProjectsList));
